import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  ClientDetails,
  ClientListOption,
  ClientRetentionOverviewSort,
  ClientsSort,
  ICreateClientParams,
} from '../api/clientsApi/interfaces';
import {
  createClient,
  deleteClient,
  getAgentClientList,
  getAreasList,
  getClient,
  getClientRetentionOverview,
  getClients,
  getClientsList,
  updateClient,
} from '../api/clientsApi';
import { AxiosError } from 'axios';
import { IPaginatedReactQueryParams } from './interfaces';

export const useGetClient = (id: number) => {
  return useQuery<ClientDetails>({
    queryFn: () => getClient(id),
    queryKey: ['client', id],
    enabled: !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCreateClientMutation = () => {
  return useMutation<number, AxiosError, ICreateClientParams>({
    mutationFn: createClient,
  });
};

export const useUpdateClientMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    AxiosError,
    { clientId: number; clientData: ICreateClientParams }
  >({
    mutationFn: async ({ clientId, clientData }) =>
      updateClient(clientId, clientData),
    onSuccess: (clientId: number) => {
      queryClient.invalidateQueries({
        queryKey: ['client', clientId],
      });
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, number>({
    mutationFn: deleteClient,
    onSuccess: (_, clientId) => {
      queryClient.removeQueries(['client', clientId]);
    },
  });
};

export const useGetClients = ({
  page,
  pageSize,
  sortField,
  search,
  filter,
}: IPaginatedReactQueryParams<ClientsSort>) => {
  return useQuery({
    queryKey: ['clients', page, pageSize, sortField, search, filter],
    queryFn: () => {
      return getClients(
        page + 1,
        pageSize,
        sortField,
        search || undefined,
        filter
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetClientsList = (enabled: boolean = true) => {
  return useQuery<ClientListOption[]>({
    queryFn: () => getClientsList(),
    queryKey: ['clients-list'],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
  });
};

export const useGetAgentClientsList = (
  agentId: number | undefined,
  enabled: boolean = true
) => {
  return useQuery<ClientListOption[]>({
    queryFn: () => getAgentClientList(agentId || 0),
    queryKey: ['agent-clients-list', agentId],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled && agentId !== undefined,
  });
};

export const useGetAreasList = (enabled: boolean = true) => {
  return useQuery<Area[]>({
    queryFn: () => getAreasList(),
    queryKey: ['areas-list'],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
  });
};

export const useGetClientRetentionOverview = ({
  page,
  pageSize,
  sortField,
  search,
  filter,
}: IPaginatedReactQueryParams<ClientRetentionOverviewSort>) => {
  return useQuery({
    queryKey: [
      'client-retention-overview',
      page,
      pageSize,
      sortField,
      search,
      filter,
    ],
    queryFn: () => {
      return getClientRetentionOverview(
        page + 1,
        pageSize,
        sortField,
        search || undefined,
        filter
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
